/**
 * Popup-based Printing Service
 * 
 * Opens a popup window on app.castlepos.com to handle QZ Tray communication.
 * This bypasses the iframe domain issue where QZ Tray sees the GHL domain.
 * 
 * Uses server-side temporary storage for print data (localStorage won't work cross-origin).
 */

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'https://app.castlepos.com';

// Determine the print popup URL
const getPrintPopupUrl = () => {
  if (BACKEND_URL.includes('castlepos.com')) {
    return 'https://app.castlepos.com/print';
  }
  // Development/preview - use current origin or backend URL
  return `${window.location.origin}/print`;
};

const PRINT_POPUP_URL = getPrintPopupUrl();

class PopupPrintingService {
  constructor() {
    this.popup = null;
    this.messageHandler = null;
    this.isConnected = false;
    this.workerWindow = null;
    this.useWorker = false;
    
    console.log('[PopupPrinting] Initialized with URL:', PRINT_POPUP_URL);
    console.log('[PopupPrinting] Backend URL:', BACKEND_URL);
  }

  /**
   * Store print job on server and get job ID
   */
  async createPrintJob(data) {
    try {
      const response = await fetch(`${BACKEND_URL}/api/qz/print-job`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      });
      
      if (!response.ok) {
        throw new Error(`Failed to create print job: ${response.status}`);
      }
      
      const result = await response.json();
      console.log('[PopupPrinting] Created print job:', result.job_id);
      return result.job_id;
    } catch (error) {
      console.error('[PopupPrinting] Failed to create print job:', error);
      throw error;
    }
  }

  /**
   * Open popup and wait for result
   */
  openPopupAndWait(jobId) {
    return new Promise((resolve, reject) => {
      // Build URL with job ID
      const url = `${PRINT_POPUP_URL}?jobId=${jobId}`;

      console.log('[PopupPrinting] Opening popup:', url);

      // Open popup window
      const width = 450;
      const height = 350;
      const left = window.screenX + (window.outerWidth - width) / 2;
      const top = window.screenY + (window.outerHeight - height) / 2;

      this.popup = window.open(
        url,
        'qz_print_popup',
        `width=${width},height=${height},left=${left},top=${top},scrollbars=no,resizable=no`
      );

      if (!this.popup) {
        reject(new Error('Failed to open print popup. Please allow popups for this site.'));
        return;
      }

      // Set timeout for popup
      const timeout = setTimeout(() => {
        cleanup();
        reject(new Error('Print operation timed out'));
      }, 60000); // 60 second timeout

      // Listen for messages from popup
      const handleMessage = (event) => {
        // Accept messages from our domains
        const validOrigins = ['castlepos.com', 'localhost', window.location.hostname];
        const isValidOrigin = validOrigins.some(domain => event.origin.includes(domain));
        
        if (!isValidOrigin) {
          return;
        }

        const { type, error, action } = event.data || {};

        if (type === 'QZ_PRINT_SUCCESS') {
          console.log('[PopupPrinting] Print success:', action);
          cleanup();
          resolve(true);
        } else if (type === 'QZ_PRINT_ERROR') {
          console.error('[PopupPrinting] Print error:', error);
          cleanup();
          reject(new Error(error || 'Print operation failed'));
        }
      };

      const cleanup = () => {
        clearTimeout(timeout);
        window.removeEventListener('message', handleMessage);
        this.messageHandler = null;
      };

      window.addEventListener('message', handleMessage);
      this.messageHandler = handleMessage;

      // Also handle popup close without message
      const checkClosed = setInterval(() => {
        if (this.popup && this.popup.closed) {
          clearInterval(checkClosed);
          // Don't cleanup/reject immediately - popup might have sent message just before closing
          setTimeout(() => {
            if (this.messageHandler) {
              cleanup();
              // Could be success or user closed - don't reject
              resolve(true);
            }
          }, 500);
        }
      }, 500);
    });
  }

  /**
   * Check if service is available
   */
  isAvailable() {
    return true;
  }

  /**
   * Initialize
   */
  async initialize() {
    console.log('[PopupPrinting] Initialize called');
    
    // Check if worker is enabled
    const receiptSettingsStr = localStorage.getItem('receipt_settings');
    const settings = receiptSettingsStr ? JSON.parse(receiptSettingsStr) : {};
    this.useWorker = settings.useBackgroundWorker === true;
    
    if (this.useWorker) {
      console.log('[PopupPrinting] Background worker enabled. Pre-warming...');
      await this.ensureWorkerReady();
    }
    
    this.isConnected = true;
    return true;
  }

  /**
   * Spawns or focuses the Background Print Worker
   */
  ensureWorkerReady() {
    return new Promise((resolve) => {
      let workerUrl = `${window.location.origin}/print-worker`;
      if (BACKEND_URL.includes('castlepos.com')) {
        workerUrl = 'https://app.castlepos.com/print-worker';
      }

      if (this.workerWindow && !this.workerWindow.closed) {
        console.log('[PopupPrinting] Worker already running.');
        resolve(this.workerWindow);
        return;
      }

      const width = 450;
      const height = 350;
      const left = window.screenX + (window.outerWidth - width) / 2;
      const top = window.screenY + (window.outerHeight - height) / 2;

      this.workerWindow = window.open(
        workerUrl,
        'qz_background_worker',
        `width=${width},height=${height},left=${left},top=${top},scrollbars=no,resizable=no`
      );

      const handleReady = (event) => {
        if (event.data?.type === 'QZ_WORKER_READY') {
          console.log('[PopupPrinting] Background worker reported READY.');
          window.removeEventListener('message', handleReady);
          resolve(this.workerWindow);
        }
      };
      window.addEventListener('message', handleReady);
      
      // Fallback timeout in case of popups blocked or already ready
      setTimeout(() => {
        window.removeEventListener('message', handleReady);
        resolve(this.workerWindow);
      }, 3000);
    });
  }

  /**
   * Dispatch job to the persistent worker via postMessage
   */
  async dispatchJobToWorker(action, data = {}) {
    console.log(`[PopupPrinting] Dispatching job '${action}' via Background Worker...`);
    await this.ensureWorkerReady();
    
    return new Promise((resolve, reject) => {
      const jobId = `job_${Date.now()}_${Math.floor(Math.random() * 1000)}`;
      const payload = {
        type: 'QZ_PRINT_JOB',
        jobId,
        action,
        ...data
      };
      
      const handleMessage = (event) => {
        if (event.data?.jobId !== jobId && action !== 'listPrinters') return;
        
        if (event.data?.type === 'QZ_PRINT_SUCCESS') {
          window.removeEventListener('message', handleMessage);
          resolve(true);
        } else if (event.data?.type === 'QZ_PRINT_ERROR') {
          window.removeEventListener('message', handleMessage);
          reject(new Error(event.data.error || 'Print failed in worker'));
        } else if (event.data?.type === 'QZ_PRINTERS_FOUND' && action === 'listPrinters') {
          window.removeEventListener('message', handleMessage);
          resolve(event.data.printers);
        }
      };
      
      window.addEventListener('message', handleMessage);
      
      // Safety timeout
      setTimeout(() => {
        window.removeEventListener('message', handleMessage);
        reject(new Error('Worker job timeout (no response after 60s)'));
      }, 60000);
      
      if (!this.workerWindow || this.workerWindow.closed) {
        window.removeEventListener('message', handleMessage);
        reject(new Error('Worker window is closed or blocked. check popup blocker!'));
        return;
      }

      this.workerWindow.postMessage(payload, '*');
    });
  }

  /**
   * List available printers
   */
  async listPrinters() {
    try {
      if (this.useWorker) {
        return await this.dispatchJobToWorker('listPrinters');
      }

      // Legacy fallback
      const cacheKey = `printers_${Date.now()}`;
      const jobId = await this.createPrintJob({ 
        action: 'listPrinters',
        cacheKey: cacheKey
      });
      
      let printersFromMessage = null;
      const messageHandler = (event) => {
        if (event.data?.type === 'QZ_PRINTERS_FOUND') {
          printersFromMessage = event.data.printers;
        }
      };
      window.addEventListener('message', messageHandler);
      await this.openPopupAndWait(jobId);
      window.removeEventListener('message', messageHandler);
      
      if (printersFromMessage && printersFromMessage.length > 0) {
        return printersFromMessage;
      }
      
      const response = await fetch(`${BACKEND_URL}/api/qz/printers/${cacheKey}`);
      if (response.ok) {
        const data = await response.json();
        return data.printers || [];
      }
      return [];
    } catch (error) {
      console.error('[PopupPrinting] listPrinters failed:', error);
      throw error;
    }
  }

  /**
   * Print HTML receipt
   */
  async printReceipt(html, options = {}) {
    const { printerName, printer, selectedPrinter, openDrawer, openCashDrawer } = options;
    const targetPrinter = printerName || printer || selectedPrinter;
    const shouldOpenDrawer = openDrawer || openCashDrawer;

    try {
      if (this.useWorker) {
        return await this.dispatchJobToWorker(shouldOpenDrawer ? 'printAndDrawer' : 'print', {
          html,
          printer: targetPrinter,
          margins: options.margins || {}
        });
      }

      // Legacy fallback
      const jobData = {
        action: shouldOpenDrawer ? 'printAndDrawer' : 'print',
        html,
        printer: targetPrinter,
        margins: options.margins || {}
      };
      
      const jobId = await this.createPrintJob(jobData);
      await this.openPopupAndWait(jobId);
      return true;
    } catch (error) {
      console.error('[PopupPrinting] printReceipt failed:', error);
      throw error;
    }
  }

  /**
   * Print HTML (alias for printReceipt)
   */
  async printHTMLReceipt(html, printerName = null) {
    return this.printReceipt(html, { printerName });
  }

  /**
   * Open cash drawer
   */
  async openCashDrawer(printerName = null) {
    try {
      if (this.useWorker) {
        return await this.dispatchJobToWorker('drawer', { printer: printerName });
      }

      // Legacy fallback
      const jobId = await this.createPrintJob({
        action: 'drawer',
        printer: printerName,
      });
      await this.openPopupAndWait(jobId);
      return true;
    } catch (error) {
      console.error('[PopupPrinting] openCashDrawer failed:', error);
      throw error;
    }
  }

  /**
   * Test print
   */
  async testPrint(printerName = null) {
    try {
      if (this.useWorker) {
        return await this.dispatchJobToWorker('test', { printer: printerName });
      }

      // Legacy fallback
      const jobId = await this.createPrintJob({
        action: 'test',
        printer: printerName,
      });
      await this.openPopupAndWait(jobId);
      return true;
    } catch (error) {
      console.error('[PopupPrinting] testPrint failed:', error);
      throw error;
    }
  }

  /**
   * Get connection status
   */
  getConnectionStatus() {
    return this.isConnected;
  }

  /**
   * Get environment info
   */
  getEnvironmentInfo() {
    return {
      platform: 'web',
      isNative: false,
      service: 'QZ Tray (Popup)',
      mode: 'popup'
    };
  }
}

export default new PopupPrintingService();
